import {
  isPdfFile,
  isDocxFile,
  validateSelectablePdf
} from './reviewablePdf';

// ALLOW_PDF_IMAGES_V1

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const API_URL =
  process.env.REACT_APP_API_URL;


function headers(
  extra = {}
) {
  return {
    apikey:
      SUPABASE_KEY,

    Authorization:
      'Bearer ' +
      SUPABASE_KEY,

    ...extra
  };
}


async function readJson(
  response,
  label
) {
  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      label +
      ' failed (' +
      response.status +
      '): ' +
      (
        text ||
        response.statusText
      )
    );
  }

  return text
    ? JSON.parse(text)
    : null;
}


export function normalizeSubmissionFileSettings(
  settings
) {
  return {
    allowPdf:
      settings?.allowed_pdf !==
      false,

    allowDocx:
      settings?.allowed_docx !==
      false,

    multiFile:
      settings
        ?.multi_file_submission ===
      true,

    allowPdfImages:
      settings
        ?.allow_pdf_images ===
      true
  };
}


export function getSubmissionFileAccept(
  settings
) {
  const config =
    normalizeSubmissionFileSettings(
      settings
    );

  const values = [];

  if (config.allowPdf) {
    values.push(
      '.pdf',
      'application/pdf'
    );
  }

  if (config.allowDocx) {
    values.push(
      '.docx',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );
  }

  return values.join(',');
}


export function getSubmissionFileFormatLabel(
  settings
) {
  const config =
    normalizeSubmissionFileSettings(
      settings
    );

  const labels = [];

  if (config.allowPdf) {
    labels.push('PDF');
  }

  if (config.allowDocx) {
    labels.push(
      'Word (.docx)'
    );
  }

  return labels.join(' or ');
}


export function getSubmissionFileExtension(
  file
) {
  const name =
    String(
      file?.name ||
      ''
    );

  const index =
    name.lastIndexOf('.');

  if (
    index < 0 ||
    index ===
      name.length - 1
  ) {
    return '';
  }

  return name
    .slice(index + 1)
    .toLowerCase();
}


export function safeSubmissionFilePart(
  value
) {
  return String(
    value || ''
  )
    .replace(
      /[\\/:*?"<>|]/g,
      '-'
    )
    .replace(
      /\s+/g,
      ' '
    )
    .trim()
    .slice(
      0,
      100
    );
}


export async function validateConfiguredSubmissionFile(
  file,
  settings
) {
  if (!file) {
    throw new Error(
      'Choose a file.'
    );
  }

  const config =
    normalizeSubmissionFileSettings(
      settings
    );

  if (
    !config.allowPdf &&
    !config.allowDocx
  ) {
    throw new Error(
      'This assignment has no permitted file formats configured.'
    );
  }

  if (
    isPdfFile(file) &&
    !config.allowPdf
  ) {
    throw new Error(
      'This assignment does not accept PDF files.'
    );
  }

  if (
    isDocxFile(file) &&
    !config.allowDocx
  ) {
    throw new Error(
      'This assignment does not accept Word (.docx) files.'
    );
  }

  if (
    !isPdfFile(file) &&
    !isDocxFile(file)
  ) {
    throw new Error(
      'This assignment accepts only PDF or Word (.docx) files.'
    );
  }

  if (
    file.size >
    50 * 1024 * 1024
  ) {
    throw new Error(
      'File too large. Maximum size is 50MB.'
    );
  }

  if (
    isPdfFile(file) &&
    !config.allowPdfImages
  ) {
    await validateSelectablePdf(
      file
    );
  }

  return true;
}


export async function fetchSubmissionFileSlots(
  courseId
) {
  if (!courseId) {
    return [];
  }

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/course_assignment_submission_file_slots' +
        '?course_id=eq.' +
        encodeURIComponent(
          courseId
        ) +
        '&select=*' +
        '&order=display_order.asc,created_at.asc',
      {
        headers:
          headers()
      }
    );

  const rows =
    await readJson(
      response,
      'Loading submission file settings'
    );

  return Array.isArray(rows)
    ? rows
    : [];
}


export async function fetchAssignmentSubmissionFiles(
  sourceSubmissionId
) {
  if (!sourceSubmissionId) {
    return [];
  }

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/assignment_submission_files' +
        '?assignment_submission_id=eq.' +
        encodeURIComponent(
          sourceSubmissionId
        ) +
        '&select=*' +
        '&order=display_order.asc,created_at.asc',
      {
        headers:
          headers()
      }
    );

  const rows =
    await readJson(
      response,
      'Loading submitted files'
    );

  return Array.isArray(rows)
    ? rows
    : [];
}


export async function fetchFilesForSubmission(
  submission
) {
  if (!submission?.id) {
    return [];
  }

  return fetchAssignmentSubmissionFiles(
    submission
      .file_source_submission_id ||
    submission.id
  );
}


async function patchSubmission(
  submissionId,
  data
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/assignment_submissions?id=eq.' +
        encodeURIComponent(
          submissionId
        ),
      {
        method:
          'PATCH',

        headers:
          headers({
            'Content-Type':
              'application/json',

            Prefer:
              'return=representation'
          }),

        body:
          JSON.stringify(
            data
          )
      }
    );

  const rows =
    await readJson(
      response,
      'Updating assignment submission'
    );

  return Array.isArray(rows)
    ? rows
    : [];
}


async function upsertSubmissionFile(
  row
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/assignment_submission_files' +
        '?on_conflict=assignment_submission_id,slot_id',
      {
        method:
          'POST',

        headers:
          headers({
            'Content-Type':
              'application/json',

            Prefer:
              'resolution=merge-duplicates,return=representation'
          }),

        body:
          JSON.stringify(
            row
          )
      }
    );

  const rows =
    await readJson(
      response,
      'Saving submitted file'
    );

  return Array.isArray(rows)
    ? rows[0] || null
    : null;
}


function mimeFromName(
  name
) {
  if (
    /\.pdf$/i.test(
      String(name || '')
    )
  ) {
    return 'application/pdf';
  }

  if (
    /\.docx$/i.test(
      String(name || '')
    )
  ) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }

  return 'application/octet-stream';
}


async function readBase64(
  file
) {
  return new Promise(
    (
      resolve,
      reject
    ) => {
      const reader =
        new FileReader();

      reader.onload =
        event => {
          const value =
            String(
              event
                .target
                ?.result ||
              ''
            );

          resolve(
            value.includes(',')
              ? value.split(',')[1]
              : value
          );
        };

      reader.onerror =
        () =>
          reject(
            new Error(
              'Could not read the selected file.'
            )
          );

      reader.readAsDataURL(
        file
      );
    }
  );
}


async function copySourceFiles(
  rows,
  targetSubmissionId
) {
  for (
    const row
    of rows
  ) {
    if (!row?.slot_id) {
      continue;
    }

    await upsertSubmissionFile({
      assignment_submission_id:
        targetSubmissionId,

      slot_id:
        row.slot_id,

      slot_label:
        row.slot_label,

      original_file_name:
        row.original_file_name,

      file_name:
        row.file_name,

      mime_type:
        row.mime_type,

      drive_file_id:
        row.drive_file_id,

      review_pdf_drive_file_id:
        row.review_pdf_drive_file_id,

      review_pdf_file_name:
        row.review_pdf_file_name,

      display_order:
        row.display_order
    });
  }
}


export async function prepareMultiFileDraft({
  submission,
  slots
}) {
  if (!submission?.id) {
    throw new Error(
      'Assignment submission does not exist.'
    );
  }

  const ownId =
    submission.id;

  const previousSourceId =
    submission
      .file_source_submission_id ||
    ownId;

  let ownFiles =
    await fetchAssignmentSubmissionFiles(
      ownId
    );

  if (
    previousSourceId !==
    ownId
  ) {
    const sourceFiles =
      await fetchAssignmentSubmissionFiles(
        previousSourceId
      );

    const existingSlotIds =
      new Set(
        ownFiles
          .map(
            row =>
              row.slot_id
          )
          .filter(Boolean)
          .map(String)
      );

    const missingCopies =
      sourceFiles.filter(
        row =>
          row.slot_id &&
          !existingSlotIds.has(
            String(
              row.slot_id
            )
          )
      );

    if (
      missingCopies.length
    ) {
      await copySourceFiles(
        missingCopies,
        ownId
      );

      ownFiles =
        await fetchAssignmentSubmissionFiles(
          ownId
        );
    }
  }

  /*
   * Backward compatibility:
   * if a previously single-file submission is converted to
   * multi-file, treat its old document as the first slot.
   */
  if (
    ownFiles.length === 0 &&
    submission.drive_file_id &&
    slots?.[0]
  ) {
    await upsertSubmissionFile({
      assignment_submission_id:
        ownId,

      slot_id:
        slots[0].id,

      slot_label:
        slots[0].label,

      original_file_name:
        submission.file_name,

      file_name:
        submission.file_name,

      mime_type:
        mimeFromName(
          submission.file_name
        ),

      drive_file_id:
        submission.drive_file_id,

      review_pdf_drive_file_id:
        submission
          .review_pdf_drive_file_id ||
        (
          /\.pdf$/i.test(
            String(
              submission.file_name ||
              ''
            )
          )
            ? submission.drive_file_id
            : null
        ),

      review_pdf_file_name:
        submission
          .review_pdf_file_name ||
        (
          /\.pdf$/i.test(
            String(
              submission.file_name ||
              ''
            )
          )
            ? submission.file_name
            : null
        ),

      display_order:
        slots[0].display_order
    });

    ownFiles =
      await fetchAssignmentSubmissionFiles(
        ownId
      );
  }

  const updatedRows =
    await patchSubmission(
      ownId,
      {
        submitted:
          false,

        submitted_at:
          null,

        file_source_submission_id:
          ownId
      }
    );

  return {
    submission:
      updatedRows[0] || {
        ...submission,

        submitted:
          false,

        submitted_at:
          null,

        file_source_submission_id:
          ownId
      },

    files:
      ownFiles
  };
}


export async function uploadMultiFileSlot({
  submission,
  slot,
  file,
  courseId,
  storedFileName,
  existingFile = null,
  allowPdfImages = false,
  skipValidation = false
}) {
  if (
    !submission?.id ||
    !slot?.id
  ) {
    throw new Error(
      'The submission file could not be resolved.'
    );
  }

  if (!skipValidation) {
    await validateConfiguredSubmissionFile(
      file,
      {
        allowed_pdf:
          slot.allow_pdf,

        allowed_docx:
          slot.allow_docx,

        allow_pdf_images:
          allowPdfImages
      }
    );
  }

  if (!API_URL) {
    throw new Error(
      'The assignment upload service is not configured.'
    );
  }

  /*
   * Temporarily point the legacy single-file fields at this
   * slot. The existing upload service can then replace the
   * correct Drive object and prepare its PDF rendition.
   *
   * submitted remains FALSE, so group-submission propagation
   * does not occur during this staging step.
   */
  const clearedRows = await patchSubmission(submission.id, {
    submitted: false,
    submitted_at: null,
    drive_file_id: null,
    file_name: null,
    review_pdf_drive_file_id: null,
    review_pdf_file_name: null,
    file_source_submission_id: submission.id
  });

  if (!clearedRows?.[0] || clearedRows[0].drive_file_id) {
    throw new Error('Could not prepare the replacement upload.');
  }

  const previousDriveId =
    existingFile
      ?.drive_file_id ||
    null;

  const base64Data =
    await readBase64(
      file
    );

  await fetch(
    API_URL,
    {
      method:
        'POST',

      mode:
        'no-cors',

      headers: {
        'Content-Type':
          'application/json'
      },

      body:
        JSON.stringify({
          action:
            'uploadAssignmentSubmission',

          submissionId:
            submission.id,

          courseId,

          fileName:
            storedFileName,

          base64Data,

          mimeType:
            skipValidation
              ? mimeFromName(file.name)
              : (file.type || mimeFromName(file.name))
        })
    }
  );

  let uploaded =
    null;

  for (
    let attempt = 0;
    attempt < 30;
    attempt += 1
  ) {
    await new Promise(
      resolve =>
        setTimeout(
          resolve,
          2000
        )
    );

    const response =
      await fetch(
        SUPABASE_URL +
          '/rest/v1/assignment_submissions' +
          '?id=eq.' +
          encodeURIComponent(
            submission.id
          ) +
          '&select=drive_file_id,file_name,review_pdf_drive_file_id,review_pdf_file_name',
        {
          headers:
            headers()
        }
      );

    const rows =
      await readJson(
        response,
        'Checking uploaded assignment file'
      );

    const row =
      Array.isArray(rows)
        ? rows[0]
        : null;

    if (
      row?.drive_file_id &&
      (
        !previousDriveId ||
        String(
          row.drive_file_id
        ) !==
          String(
            previousDriveId
          )
      )
    ) {
      uploaded =
        row;

      break;
    }
  }

  if (!uploaded) {
    throw new Error(
      'The file upload did not complete. The assignment has not been submitted.'
    );
  }

  const saved =
    await upsertSubmissionFile({
      assignment_submission_id:
        submission.id,

      slot_id:
        slot.id,

      slot_label:
        slot.label,

      original_file_name:
        file.name,

      file_name:
        uploaded.file_name ||
        storedFileName,

      mime_type:
        skipValidation
          ? mimeFromName(file.name)
          : (file.type || mimeFromName(file.name)),

      drive_file_id:
        uploaded.drive_file_id,

      review_pdf_drive_file_id:
        uploaded
          .review_pdf_drive_file_id ||
        null,

      review_pdf_file_name:
        uploaded
          .review_pdf_file_name ||
        null,

      display_order:
        slot.display_order
    });

  /*
   * Do not leave an arbitrary slot masquerading as the
   * submission's primary file while the package is still a
   * draft. The database chooses the primary file on Submit.
   */
  await patchSubmission(
    submission.id,
    {
      drive_file_id:
        null,

      file_name:
        null,

      review_pdf_drive_file_id:
        null,

      review_pdf_file_name:
        null
    }
  );

  return saved;
}


export async function finalizeMultiFileSubmission({
  submission,
  slots,
  extraData = {}
}) {
  if (!submission?.id) {
    throw new Error(
      'Assignment submission does not exist.'
    );
  }

  const files =
    await fetchAssignmentSubmissionFiles(
      submission.id
    );

  if (!files.length) {
    throw new Error(
      'Choose at least one submission file.'
    );
  }

  const fileSlotIds =
    new Set(
      files
        .map(
          row =>
            row.slot_id
        )
        .filter(Boolean)
        .map(String)
    );

  const missing =
    (Array.isArray(slots)
      ? slots
      : []
    )
      .filter(
        slot =>
          slot.required ===
            true &&
          !fileSlotIds.has(
            String(
              slot.id
            )
          )
      );

  if (missing.length) {
    throw new Error(
      'Required submission file missing: ' +
      missing[0].label +
      '.'
    );
  }

  const rows =
    await patchSubmission(
      submission.id,
      {
        ...extraData,

        file_source_submission_id:
          submission.id,

        submitted:
          true,

        submitted_at:
          new Date()
            .toISOString()
      }
    );

  if (!rows[0]) {
    throw new Error(
      'The files uploaded, but the assignment could not be submitted.'
    );
  }

  return rows[0];
}
